/**
 * Tool-call revision tracking.
 *
 * A tool result may declare `replaces: <toolCallId>` (see
 * `ToolResult.replaces`) to supersede an earlier call. Each result carries
 * at most one backward edge, so the edges form a forest: several calls may
 * point at the same target, and a call may point at an already-superseded
 * one. This module derives every projection read-side from that minimal
 * fact — groups (by root), the current revision (the chronologically last
 * group member), model-citable revision ids (`<toolName>-<n>`), and UI
 * adjacency — so display policy can change without any data migration.
 *
 * Strictness is split by boundary: a model-provided `replaces` reference
 * that cannot be resolved fails the call outright (`resolveReplacesArg`
 * throws before execute runs), while a stored edge whose target is missing
 * (trimmed or corrupt history) is tolerated — the call simply stands alone.
 */
import { parseResultEnvelope } from '#plugin-chat/lib/tool/result-envelope.ts';

type RevisionScanPart = {
  readonly type?: string;
  readonly toolName?: string;
  readonly toolCallId?: string;
  readonly text?: string;
  readonly args?: unknown;
  readonly input?: unknown;
  readonly result?: unknown;
  readonly output?: unknown;
};

/**
 * The structural slice of a thread message the revision scans read. Live
 * assistant-ui `MessageState`/`UIMessage` messages carry `parts` (tool
 * parts typed `tool-<name>` with `input`/`output`), persisted
 * `ThreadMessage`s carry `content` (tool parts typed `tool-call` with
 * `args`/`result`); the scans touch only the overlapping fields.
 */
export type RevisionScanMessage = {
  readonly role?: string;
  readonly parts?: readonly RevisionScanPart[];
  readonly content?: readonly RevisionScanPart[] | string;
};

/** One tool call as the revision index sees it, in thread order. */
export type ToolCallRevision = {
  readonly toolCallId: string;
  readonly toolName: string;
  /** Model-citable id: `<toolName>-<n>`, 1-based per tool in thread order. Stable because history is append-only — supersession annotates, never deletes. */
  readonly revisionId: string;
  readonly args: unknown;
  readonly output: unknown;
};

type TimelineEvent = { readonly kind: 'content' } | { readonly kind: 'tool'; readonly toolCallId: string };

export type RevisionIndex = {
  readonly byCallId: ReadonlyMap<string, ToolCallRevision>;
  readonly byRevisionId: ReadonlyMap<string, ToolCallRevision>;
  /** Member toolCallId → its full group in chronological order (root first). Only multi-member groups are indexed; a call with no revisions is absent. */
  readonly groupOf: ReadonlyMap<string, readonly ToolCallRevision[]>;
  /** Flat thread-order stream of tool calls and user-visible content, for adjacency checks. */
  readonly timeline: readonly TimelineEvent[];
};

const partsOf = (message: RevisionScanMessage): readonly RevisionScanPart[] =>
  message.parts ?? (Array.isArray(message.content) ? message.content : []);

const isToolCallPart = (part: RevisionScanPart): boolean =>
  typeof part.toolCallId === 'string' &&
  (part.type === 'tool-call' || part.type === 'dynamic-tool' || (part.type?.startsWith('tool-') ?? false));

const partToolName = (part: RevisionScanPart): string | undefined => {
  if (typeof part.toolName === 'string') {
    return part.toolName;
  }
  if (part.type !== undefined && part.type !== 'tool-call' && part.type.startsWith('tool-')) {
    return part.type.slice('tool-'.length);
  }
  return undefined;
};

const isContentPart = (part: RevisionScanPart): boolean =>
  part.type === 'text' && typeof part.text === 'string' && part.text.trim().length > 0;

/**
 * The toolCallId a settled result validly supersedes. Only a *successful*
 * result carries an outgoing edge — a failed correction must not hide the
 * prior good revision — and a dangling target is dropped read-side.
 */
const replacesTarget = (
  entry: ToolCallRevision,
  byCallId: ReadonlyMap<string, ToolCallRevision>,
): string | undefined => {
  if (typeof entry.output !== 'object' || entry.output === null) {
    return undefined;
  }
  if (parseResultEnvelope(entry.output).kind === 'failure') {
    return undefined;
  }
  const { replaces } = entry.output as { replaces?: unknown };
  if (typeof replaces !== 'string') {
    return undefined;
  }
  return byCallId.has(replaces) ? replaces : undefined;
};

/** Build the revision index for a thread. Pure; cheap at thread scale. */
export const buildRevisionIndex = (messages: readonly RevisionScanMessage[]): RevisionIndex => {
  const flatParts = messages.flatMap(partsOf);
  const timeline = flatParts.flatMap<TimelineEvent>((part) => {
    if (isToolCallPart(part)) {
      return [{ kind: 'tool', toolCallId: part.toolCallId as string }];
    }
    return isContentPart(part) ? [{ kind: 'content' }] : [];
  });

  const entries = flatParts.reduce<{ counts: Record<string, number>; list: ToolCallRevision[] }>(
    (acc, part) => {
      const toolName = partToolName(part);
      if (!isToolCallPart(part) || toolName === undefined) {
        return acc;
      }
      const n = (acc.counts[toolName] ?? 0) + 1;
      acc.counts[toolName] = n;
      acc.list.push({
        toolCallId: part.toolCallId as string,
        toolName,
        revisionId: `${toolName}-${n}`,
        args: part.args ?? part.input,
        output: part.result ?? part.output,
      });
      return acc;
    },
    { counts: {}, list: [] },
  ).list;

  const byCallId = new Map(entries.map((entry) => [entry.toolCallId, entry]));
  const parentOf = new Map(
    entries.flatMap((entry) => {
      const target = replacesTarget(entry, byCallId);
      return target === undefined ? [] : [[entry.toolCallId, target] as const];
    }),
  );
  // Edges point strictly backwards in an append-only thread, so cycles are
  // impossible for data this code wrote — the `seen` guard only defends the
  // deserialization boundary (corrupt persisted history must not hang render).
  const rootOf = (id: string, seen: ReadonlySet<string>): string => {
    const parent = parentOf.get(id);
    return parent === undefined || seen.has(parent) ? id : rootOf(parent, new Set([...seen, id]));
  };

  const grouped = entries.reduce<Map<string, ToolCallRevision[]>>((acc, entry) => {
    const root = rootOf(entry.toolCallId, new Set());
    acc.set(root, [...(acc.get(root) ?? []), entry]);
    return acc;
  }, new Map());
  const groupOf = new Map(
    [...grouped.values()]
      .filter((members) => members.length > 1)
      .flatMap((members) =>
        members.map((member) => [member.toolCallId, members as readonly ToolCallRevision[]] as const),
      ),
  );

  return {
    byCallId,
    byRevisionId: new Map(entries.map((entry) => [entry.revisionId, entry])),
    groupOf,
    timeline,
  };
};

/** The group's current revision — the chronologically last member (thread order is the total order; which parent each member cited does not matter). */
export const currentRevision = (members: readonly ToolCallRevision[]): ToolCallRevision =>
  members.at(-1) as ToolCallRevision;

/** The revision superseding `toolCallId`, or undefined when it is current or ungrouped. */
export const supersededBy = (index: RevisionIndex, toolCallId: string): ToolCallRevision | undefined => {
  const members = index.groupOf.get(toolCallId);
  if (!members) {
    return undefined;
  }
  const current = currentRevision(members);
  return current.toolCallId === toolCallId ? undefined : current;
};

/**
 * True when no user-visible content sits between the superseded call and
 * its group's current revision — no non-blank text and no tool call from
 * outside the group. Adjacent superseded parts render as nothing (a
 * "revised" stub directly above its own replacement is noise); distant
 * ones render a jump-to-latest stub so the original spot is never a
 * confusing hole.
 */
export const isAdjacentToCurrent = (index: RevisionIndex, toolCallId: string): boolean => {
  const members = index.groupOf.get(toolCallId);
  if (!members) {
    return false;
  }
  const memberIds = new Set(members.map((member) => member.toolCallId));
  const from = index.timeline.findIndex((event) => event.kind === 'tool' && event.toolCallId === toolCallId);
  const to = index.timeline.findIndex(
    (event) => event.kind === 'tool' && event.toolCallId === currentRevision(members).toolCallId,
  );
  if (from === -1 || to === -1) {
    return false;
  }
  return index.timeline.slice(from + 1, to).every((event) => event.kind === 'tool' && memberIds.has(event.toolCallId));
};

/** How one tool-call part should render given the thread's revision state. */
export type RevisionView =
  | { readonly kind: 'sole' }
  | { readonly kind: 'current'; readonly members: readonly ToolCallRevision[] }
  | { readonly kind: 'superseded'; readonly adjacent: boolean; readonly current: ToolCallRevision };

export const revisionView = (
  messages: readonly RevisionScanMessage[],
  toolCallId: string | undefined,
): RevisionView => {
  if (toolCallId === undefined) {
    return { kind: 'sole' };
  }
  const index = buildRevisionIndex(messages);
  const members = index.groupOf.get(toolCallId);
  if (!members) {
    return { kind: 'sole' };
  }
  const current = currentRevision(members);
  if (current.toolCallId === toolCallId) {
    return { kind: 'current', members };
  }
  return { kind: 'superseded', adjacent: isAdjacentToCurrent(index, toolCallId), current };
};

/**
 * Resolve a model-provided `replaces` revision id against the thread.
 * Hard failure by design: an unknown or cross-tool reference throws —
 * the `<Tool>` execute boundary surfaces it to the model as a structured
 * tool failure, before the tool executes — listing the valid ids so the
 * model can self-correct.
 */
export const resolveReplacesArg = (
  messages: readonly RevisionScanMessage[],
  toolName: string,
  revisionId: string,
): ToolCallRevision => {
  const index = buildRevisionIndex(messages);
  const entry = index.byRevisionId.get(revisionId);
  if (entry?.toolName === toolName) {
    return entry;
  }
  const valid = [...index.byCallId.values()]
    .filter((candidate) => candidate.toolName === toolName)
    .map((candidate) => candidate.revisionId);
  const validNote =
    valid.length > 0
      ? `Existing ${toolName} revision ids: ${valid.join(', ')}.`
      : `No earlier ${toolName} call exists to replace.`;
  throw new Error(
    `Unknown revision id "${revisionId}" for ${toolName}. ${validNote} Re-issue the call with a valid replaces id, or omit replaces to render a new result.`,
  );
};

/**
 * The `replaces` target implied by the previous call's flow signal: the
 * latest settled tool call on the thread, when it is the *same* tool and
 * its result declared `flowSignal.replacedByNextCall` — i.e. the call now
 * executing is the forced correction that signal announced. Deterministic:
 * no model cooperation and no argument matching involved.
 */
export const forcedReplacementTarget = (
  messages: readonly RevisionScanMessage[],
  toolName: string,
): string | undefined => {
  const index = buildRevisionIndex(messages);
  const latest = [...index.byCallId.values()].filter((entry) => entry.output != null).at(-1);
  if (!latest || latest.toolName !== toolName) {
    return undefined;
  }
  const output = latest.output as { flowSignal?: { replacedByNextCall?: unknown } };
  return output.flowSignal?.replacedByNextCall === true ? latest.toolCallId : undefined;
};
